/**
 * Admin deep links for paper-journal row identifiers (HashRouter paths).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { formatPaperJournalPlainInt } from '@/pages/PaperJournal/paperJournalFormat';

import styles from './paperJournalAdminLinks.module.scss';

export function paperJournalUserPath(refUserId: number): string {
  return `/users/${refUserId}`;
}

export function paperJournalEpidPath(eligibleParticipantId: string | number): string {
  return `/incomm-cards/${eligibleParticipantId}`;
}

export function paperJournalPartnerPath(partnerProductId: number): string {
  return `/partners/${partnerProductId}`;
}

export function paperJournalRewardPath(rewardId: string): string {
  return `/rewards/${rewardId}`;
}

export function paperJournalTransactionPath(transactionId: string): string {
  return `/transactions/${transactionId}`;
}

export function paperJournalFulfillmentPath(fulfillmentId: string): string {
  return `/fulfillments/${fulfillmentId}`;
}

export function paperJournalUploadPath(fileSha256: string): string {
  return `/paper-journal/uploads/${fileSha256}`;
}

interface PaperJournalEntityLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  /** Stop row expand/collapse when the link is inside a clickable header. */
  stopPropagation?: boolean;
}

export function PaperJournalEntityLink({
  to,
  children,
  className = '',
  stopPropagation = false,
}: PaperJournalEntityLinkProps): React.ReactElement {
  return (
    <Link
      to={to}
      className={`${styles.entityLink} ${className}`.trim()}
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
    >
      {children}
    </Link>
  );
}

export function paperJournalIdLink(
  id: string | null | undefined,
  pathForId: (id: string) => string,
): React.ReactNode {
  if (id === null || id === undefined || id === '') {
    return '—';
  }
  return (
    <PaperJournalEntityLink to={pathForId(id)} className={styles.mono}>
      {id}
    </PaperJournalEntityLink>
  );
}

export function paperJournalRefUserLink(
  refUserId: number,
  options?: { stopPropagation?: boolean },
): React.ReactNode {
  return (
    <PaperJournalEntityLink to={paperJournalUserPath(refUserId)} stopPropagation={options?.stopPropagation}>
      {formatPaperJournalPlainInt(refUserId)}
    </PaperJournalEntityLink>
  );
}

export function paperJournalEpidLink(
  eligibleParticipantId: string | number,
  options?: { stopPropagation?: boolean },
): React.ReactNode {
  const label = formatPaperJournalPlainInt(eligibleParticipantId).trim();
  if (label === '' || label === '0') {
    return <span className={styles.mono}>{label === '' ? '—' : label}</span>;
  }
  return (
    <PaperJournalEntityLink
      to={paperJournalEpidPath(eligibleParticipantId)}
      stopPropagation={options?.stopPropagation}
    >
      {label}
    </PaperJournalEntityLink>
  );
}

export function paperJournalPartnerLink(
  partnerProductId: number,
  options?: { stopPropagation?: boolean },
): React.ReactNode {
  return (
    <PaperJournalEntityLink
      to={paperJournalPartnerPath(partnerProductId)}
      stopPropagation={options?.stopPropagation}
    >
      {formatPaperJournalPlainInt(partnerProductId)}
    </PaperJournalEntityLink>
  );
}
